import { type Format } from '@ugin/shared';
import { Trophy } from 'lucide-react';
import { type ReactNode } from 'react';

import { type WidgetComponent, type WidgetMeta } from '../_base/types';
import { WidgetContainer } from '../_base/WidgetContainer';

import { type Bucket } from '@/api/types';
import { useStats } from '@/hooks/useArena';
import { formatRecord, provisionalNote, winRateText } from '@/lib/stats';


const meta: WidgetMeta = {
  id: 'record',
  name: 'Your record',
  description: 'Every number carries the games behind it.',
  icon: Trophy,
  defaultSize: { w: 5, h: 5 },
  category: 'performance',
};

export interface RecordWidgetProps {
  format?: Format;
}

export const RecordWidget: WidgetComponent<RecordWidgetProps> = ({ format }) => {
  const { data, isLoading, error, refetch } = useStats(format);
  const record = data?.record;
  const note = record ? provisionalNote(record.n) : null;

  return (
    <WidgetContainer
      meta={meta}
      icon={<Trophy className="size-4" />}
      loading={isLoading}
      error={error ?? undefined}
      onRetry={() => void refetch()}
      empty={!isLoading && record?.n === 0}
      emptyTitle="No matches yet"
      emptyDescription="Play a game in Arena and Ugin will start keeping score."
    >
      {data && record && record.n > 0 ? (
        <div className="space-y-5">
          <div className="flex items-baseline gap-4">
            <span className="font-display text-5xl tracking-wide text-ugin-text">
              {formatRecord(record)}
            </span>
            <span className="font-mono text-xl text-ugin-accent">{winRateText(record.winRate)}</span>
          </div>
          {note ? <p className="text-xs text-ugin-warn">{note}</p> : null}

          <Section title="On the play / on the draw">
            <Line label="On the play" bucket={data.byPlayDraw.onPlay} />
            <Line label="On the draw" bucket={data.byPlayDraw.onDraw} />
          </Section>

          {data.byMulligans.length > 0 ? (
            <Section title="By mulligans taken">
              {data.byMulligans.map((m) => (
                <Line
                  key={m.mulligans}
                  label={
                    m.mulligans === 0
                      ? 'Kept seven'
                      : m.mulligans === 1
                        ? 'One mulligan'
                        : `${String(m.mulligans)}+ mulligans`
                  }
                  bucket={m}
                />
              ))}
            </Section>
          ) : null}

          {data.rank ? (
            <Section title="Rank">
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-ugin-text">
                  {/* A level without a tier is meaningless: Arena reports
                      level 4 of nothing before the first ranked game. */}
                  {data.rank.constructedClass === null
                    ? 'Unranked'
                    : data.rank.constructedLevel > 0
                      ? `${data.rank.constructedClass} ${String(data.rank.constructedLevel)}`
                      : data.rank.constructedClass}
                </span>
                <span className="font-mono text-xs text-ugin-text-muted">
                  season {data.rank.wins}–{data.rank.losses}
                </span>
              </div>
            </Section>
          ) : null}
        </div>
      ) : null}
    </WidgetContainer>
  );
};

RecordWidget.meta = meta;

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <h3 className="text-xs uppercase tracking-wider text-ugin-text-faint">{title}</h3>
      <dl className="space-y-1">{children}</dl>
    </div>
  );
}

function Line({ label, bucket }: { label: string; bucket: Bucket }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <dt className="text-ugin-text-muted">{label}</dt>
      <dd className="flex items-baseline gap-3">
        <span className="font-mono text-ugin-text">{formatRecord(bucket)}</span>
        <span
          className="w-12 text-right font-mono text-xs text-ugin-text-faint"
          title={bucket.provisional ? `Provisional — ${String(bucket.n)} of 20 matches` : undefined}
        >
          {winRateText(bucket.winRate)}
        </span>
      </dd>
    </div>
  );
}
